import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { staffAPI, servicesAPI, importAPI } from '../../api'
import { useForm } from 'react-hook-form'
import { RiAddLine, RiEditLine, RiDeleteBinLine, RiLoader4Line, RiCloseLine } from 'react-icons/ri'
import toast from 'react-hot-toast'
import CsvImportButton from '../../components/admin/CsvImportButton'

function StaffModal({ staff, services, onClose }) {
  const qc = useQueryClient()
  const isEdit = !!staff

  const { register, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: staff || { name: '', email: '', phone: '', speciality: '', bio: '', working_hours_start: '09:00', working_hours_end: '18:00' },
  })

  const mutation = useMutation({
    mutationFn: (data) => isEdit ? staffAPI.update(staff.id, data) : staffAPI.create(data),
    onSuccess: () => {
      qc.invalidateQueries(['admin-staff'])
      toast.success(isEdit ? 'Staff updated' : 'Staff added')
      onClose()
    },
    onError: () => toast.error('Operation failed'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-salon-lg w-full max-w-lg p-7 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-salon-dark text-xl font-bold">{isEdit ? 'Edit Staff' : 'Add Staff Member'}</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-salon-cream transition-colors">
            <RiCloseLine className="text-xl text-salon-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="input-label">Full Name *</label>
              <input {...register('name', { required: true })} className="input-field" placeholder="Staff name" />
            </div>
            <div>
              <label className="input-label">Email *</label>
              <input {...register('email', { required: true })} type="email" className="input-field" placeholder="Email" />
            </div>
            <div>
              <label className="input-label">Phone *</label>
              <input {...register('phone', { required: true })} className="input-field" placeholder="Phone" />
            </div>
            <div className="col-span-2">
              <label className="input-label">Speciality</label>
              <input {...register('speciality')} className="input-field" placeholder="e.g. Hair Coloring Specialist" />
            </div>
            <div>
              <label className="input-label">Work Start</label>
              <input {...register('working_hours_start')} type="time" className="input-field" />
            </div>
            <div>
              <label className="input-label">Work End</label>
              <input {...register('working_hours_end')} type="time" className="input-field" />
            </div>
            <div className="col-span-2">
              <label className="input-label">Bio</label>
              <textarea {...register('bio')} className="input-field min-h-[80px] resize-none" placeholder="Short biography..." />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? <RiLoader4Line className="animate-spin" /> : isEdit ? 'Save Changes' : 'Add Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function ManageStaff() {
  const qc = useQueryClient()
  const [modal, setModal] = useState(null) // null | 'add' | staff object

  const { data: staff, isLoading } = useQuery({
    queryKey: ['admin-staff'],
    queryFn: () => staffAPI.getAll(),
    select: (r) => r.data?.data || [],
  })

  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: () => servicesAPI.getAll(),
    select: (r) => r.data?.data || [],
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => staffAPI.delete(id),
    onSuccess: () => { qc.invalidateQueries(['admin-staff']); toast.success('Staff removed') },
    onError: () => toast.error('Delete failed'),
  })

  const handleDelete = (id) => {
    if (window.confirm('Remove this staff member?')) deleteMutation.mutate(id)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-salon-dark text-2xl font-bold">Staff Members</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <CsvImportButton
            label="Import staff"
            uploader={(file) => importAPI.staff(file)}
            templateUrl="/templates/staff.csv"
            templateHeaders="name, email, role, phone, bio, instagram, specialties (semi;separated), is_active, password"
            invalidateKeys={[['admin-staff'], ['staff']]}
          />
          <button onClick={() => setModal('add')} className="btn-primary text-sm">
            <RiAddLine /> Add Staff
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><RiLoader4Line className="animate-spin text-3xl text-salon-primary" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(staff || []).map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-salon-cream-dark p-5 shadow-salon-sm">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-salon-primary/10 flex items-center justify-center overflow-hidden">
                  {s.avatar
                    ? <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />
                    : <span className="font-display text-salon-primary text-xl font-bold">{s.name.charAt(0)}</span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold font-body text-salon-dark">{s.name}</p>
                  <p className="text-xs text-salon-muted font-body truncate">{s.speciality || 'Stylist'}</p>
                </div>
              </div>

              <div className="space-y-1 text-xs font-body text-salon-muted mb-4">
                <p>{s.email}</p>
                <p>{s.phone}</p>
                <p>Hours: {s.working_hours_start} – {s.working_hours_end}</p>
              </div>

              <div className="flex gap-2">
                <button onClick={() => setModal(s)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-salon-cream
                             text-salon-primary text-xs font-semibold font-body hover:bg-salon-primary/10 transition-colors">
                  <RiEditLine /> Edit
                </button>
                <button onClick={() => handleDelete(s.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-50
                             text-red-500 text-xs font-semibold font-body hover:bg-red-100 transition-colors">
                  <RiDeleteBinLine /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <StaffModal
          staff={modal === 'add' ? null : modal}
          services={services}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}
